import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { ElMessage } from 'element-plus'
import i18n from '@/i18n'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/Index.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/dashboard/Index.vue'),
    meta: { titleKey: 'menu.dashboard', permission: 'business:dashboard' }
  },
  {
    path: '/business-orders',
    name: 'BusinessOrders',
    component: () => import('@/views/businessOrders/Index.vue'),
    meta: { titleKey: 'menu.businessOrderManage', permission: 'business:order-list' }
  },
  {
    path: '/business-orders/:id',
    name: 'BusinessOrderDetail',
    component: () => import('@/views/businessOrders/Detail.vue'),
    meta: { titleKey: 'menu.businessOrderDetail', permission: 'business:order-list' }
  },
  {
    path: '/route-workbench',
    name: 'RouteWorkbench',
    component: () => import('@/views/entry/Index.vue'),
    meta: { titleKey: 'menu.routeWorkbench', permission: 'business:route-workbench' }
  },
  {
    path: '/cs-workbench',
    name: 'CsWorkbench',
    component: () => import('@/views/cs-workbench/Index.vue'),
    meta: { titleKey: 'menu.csWorkbench', permission: 'business:cs-workbench' }
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/users/Index.vue'),
    meta: { titleKey: 'menu.system.users', permission: 'system:user' }
  },
  {
    path: '/dictionary',
    name: 'Dictionary',
    component: () => import('@/views/dictionary/Index.vue'),
    meta: { titleKey: 'menu.system.dictionary', permission: 'system:dictionary' }
  },
  {
    path: '/system/role',
    name: 'RoleManagement',
    component: () => import('@/views/system/role/index.vue'),
    meta: { titleKey: 'menu.system.role', permission: 'system:role' }
  },
  {
    path: '/system/permission',
    name: 'PermissionManagement',
    component: () => import('@/views/system/permission/index.vue'),
    meta: { titleKey: 'menu.system.permission', permission: 'system:permission' }
  },
  {
    path: '/system/organization',
    name: 'OrganizationManagement',
    component: () => import('@/views/system/organization/index.vue'),
    meta: { titleKey: 'menu.system.organization', permission: 'system:organization' }
  },
  {
    path: '/system/account-lock',
    name: 'AccountLockManagement',
    component: () => import('@/views/system/AccountLockManage.vue'),
    meta: { titleKey: 'menu.accountLock', permission: 'system:user' }
  },
  {
    path: '/agreements',
    name: 'Agreements',
    component: () => import('@/views/agreement/AgreementView.vue'),
    meta: { titleKey: 'menu.agreement', permission: 'business:agreement' }
  },
  {
    path: '/agreement-orders',
    name: 'AgreementOrders',
    component: () => import('@/views/agreement/AgreementOrderView.vue'),
    meta: { titleKey: 'menu.agreementOrders', permission: 'business:agreement-order' }
  },
  {
    path: '/customers',
    name: 'CustomerManagement',
    component: () => import('@/views/customer/CustomerView.vue'),
    meta: { titleKey: 'menu.customers', permission: 'business:customer' }
  },
  {
    path: '/product-order-records',
    name: 'ProductOrderRecords',
    component: () => import('@/views/productOrderRecord/Index.vue'),
    meta: { titleKey: 'menu.productOrderRecords', permission: 'business:product-order-records' }
  },
  {
    path: '/system/config',
    name: 'SystemConfig',
    component: () => import('@/views/system/config/Index.vue'),
    meta: { titleKey: 'menu.system.config', permission: 'system:config' }
  },
  {
    path: '/system/currency',
    name: 'CurrencyManagement',
    component: () => import('@/views/system/currency/Index.vue'),
    meta: { titleKey: 'menu.system.currency', permission: 'system:currency:view' }
  },
  {
    path: '/system/pallet-type',
    name: 'PalletTypeManagement',
    component: () => import('@/views/system/palletType/Index.vue'),
    meta: { titleKey: 'menu.system.palletType', permission: 'system:pallet-type:view' }
  },
  {
    path: '/system/mawb-pool',
    name: 'MawbPoolManagement',
    component: () => import('@/views/system/mawbPool/Index.vue'),
    meta: { titleKey: 'menu.system.mawbPool', permission: 'system:mawb-pool:view' }
  },
  {
    path: '/system/login-log',
    name: 'LoginLog',
    component: () => import('@/views/system/loginLog/Index.vue'),
    meta: { titleKey: 'menu.system.loginLog', permission: 'system:login-log' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

function getFirstAuthorizedRoute(authStore: { hasPermission: (permission: string) => boolean }): string | null {
  const routeOrder = [
    { path: '/dashboard', permission: 'business:dashboard' },
    { path: '/route-workbench', permission: 'business:route-workbench' },
    { path: '/cs-workbench', permission: 'business:cs-workbench' },
    { path: '/business-orders', permission: 'business:order-list' },
    { path: '/agreements', permission: 'business:agreement' },
    { path: '/agreement-orders', permission: 'business:agreement-order' },
    { path: '/customers', permission: 'business:customer' },
    { path: '/product-order-records', permission: 'business:product-order-records' },
    { path: '/users', permission: 'system:user' },
    { path: '/system/role', permission: 'system:role' },
    { path: '/system/permission', permission: 'system:permission' },
    { path: '/system/organization', permission: 'system:organization' },
    { path: '/system/config', permission: 'system:config' },
    { path: '/system/currency', permission: 'system:currency:view' },
    { path: '/system/pallet-type', permission: 'system:pallet-type:view' },
    { path: '/system/login-log', permission: 'system:login-log' },
    { path: '/dictionary', permission: 'system:dictionary' },
  ]
  for (const route of routeOrder) {
    if (authStore.hasPermission(route.permission)) {
      return route.path
    }
  }
  return null
}

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const t = i18n.global.t

  // 如果有token但没有用户信息，先获取用户信息
  if (authStore.isLoggedIn && !authStore.userInfo) {
    try {
      await authStore.fetchUserInfo()
    } catch (error) {
      authStore.logout()
      if (!to.meta.public) {
        next('/login')
        return
      }
    }
  }

  // 未登录且访问非公开页面，重定向到登录页
  if (!authStore.isLoggedIn && !to.meta.public) {
    next('/login')
    return
  }

  // 检查是否需要特定权限（RBAC权限控制）
  if (to.meta.permission && authStore.isLoggedIn) {
    const hasPermission = authStore.hasPermission(to.meta.permission as string)
    if (!hasPermission) {
      if (to.path === '/dashboard') {
        const fallbackRoute = getFirstAuthorizedRoute(authStore)
        if (fallbackRoute) {
          next(fallbackRoute)
        } else {
          ElMessage.error(t('msg.error.noPageAccess'))
          authStore.logout()
          next('/login')
        }
      } else {
        ElMessage.error(t('msg.error.noPermission'))
        next('/dashboard')
      }
      return
    }
  }

  next()
})

// 浏览器标签页标题：按 titleKey 翻译
router.afterEach((to) => {
  const titleKey = to.meta?.titleKey as string | undefined
  if (titleKey) {
    document.title = i18n.global.t(titleKey)
  }
})

export default router
